import { handleAuthError, isAuthError } from "../../utils/auth/authErrorHandler";

class WebSocketService {
  constructor(url) {
    this.url = url;
    this.socket = null;
    this.listeners = new Map();

    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 5;
    this.reconnectDelay = 1000;

    this.messageQueue = [];
    this.isConnected = false;
    this.reconnectTimeout = null;
    this.isAuthError = false; // Track if we've encountered an auth error

    this.connect();
  }

  connect() {
    if (this.socket || this.isAuthError) {
      return;
    }

    this.socket = new WebSocket(this.url);

    this.socket.onopen = () => {
      this.reconnectAttempts = 0;
      this.isConnected = true;

      this.flushMessageQueue();
    };

    this.socket.onmessage = (event) => {
      const data = JSON.parse(event.data);

      if (this.listeners.has(data.type)) {
        this.listeners
          .get(data.type)
          .forEach((callback) => callback(data.payload));
      }
    };

    this.socket.onclose = (event) => {
      // Check if this is an authentication error based on close code
      if (event.code === 1008 || event.code === 1002 || event.code === 1003) {
        // These codes often indicate authentication/authorization issues
        this.isAuthError = true;
        this.isConnected = false;
        handleAuthError();
        return; // Don't reconnect on auth errors
      }
      
      this.isConnected = false;
      this.reconnect();
    };

    this.socket.onerror = (error) => {
      // Check if this is an authentication error
      if (isAuthError(error)) {
        this.isAuthError = true;
        this.isConnected = false;
        handleAuthError();
        return; // Don't close and reconnect on auth errors
      }
      
      this.socket.close(); // Triggers onclose
    };
  }

  reconnect() {
    if (this.isAuthError) return; // Don't reconnect on auth errors
    
    if (this.reconnectAttempts < this.maxReconnectAttempts) {
      this.reconnectAttempts++;
      const delay = this.reconnectDelay * this.reconnectAttempts;

      this.reconnectTimeout = setTimeout(() => {
        this.connect();
      }, delay);
    }
  }

  flushMessageQueue() {
    while (this.messageQueue.length > 0 && this.isConnected) {
      const message = this.messageQueue.shift();
      this.socket.send(JSON.stringify(message));
    }
  }

  sendMessage(message) {
    if (this.isConnected && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(message));
    } else {
      this.messageQueue.push(message);
    }
  }

  addListener(event, callback) {
    if (!this.listeners.has(event)) {
      this.listeners.set(event, new Set());
    }
    this.listeners.get(event).add(callback);
  }

  removeListener(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
      if (this.listeners.get(event).size === 0) {
        this.listeners.delete(event);
      }
    }
  }

  close() {
    clearTimeout(this.reconnectTimeout);
    this.isConnected = false;
    if (this.socket) {
      this.socket.close();
    }
  }

  onConnect(callback) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      callback();
    } else {
      const interval = setInterval(() => {
        if (this.socket && this.socket.readyState === WebSocket.OPEN) {
          clearInterval(interval);
          callback();
        }
      }, 50);
    }
  }
}

// Function to create WebSocket instances for different paths
const createWebSocketClient = (path) =>
  new WebSocketService(`${process.env.REACT_APP_WS_API_DOMAIN}/${path}`);

export default createWebSocketClient;
